/**
 * セキュリティ関連のユーティリティ関数
 */

interface EscapeMap {
  [key: string]: string;
}

/**
 * HTMLエスケープ処理
 * @param str - エスケープする文字列
 * @returns エスケープされた文字列
 */
export const escapeHtml = (str: unknown): unknown => {
  if (typeof str !== 'string') return str;

  const htmlEscapeMap: EscapeMap = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
    '/': '&#x2F;'
  };

  return str.replace(/[&<>"'/]/g, (char) => htmlEscapeMap[char]);
};

/**
 * XSS攻撃のパターンを検出
 * @param input - チェックする入力値
 * @returns 危険なパターンが含まれている場合はtrue
 */
export const detectXSSPatterns = (input: unknown): boolean => {
  if (typeof input !== 'string') return false;

  // 危険なパターンのリスト
  const dangerousPatterns = [
    /<script[^>]*>/gi,
    /<\/script>/gi,
    /javascript:/gi,
    /on\w+\s*=/gi, // onclick, onload, etc.
    /<iframe[^>]*>/gi,
    /<object[^>]*>/gi,
    /<embed[^>]*>/gi,
    /<link[^>]*>/gi,
    /eval\s*\(/gi,
    /expression\s*\(/gi,
    /<img[^>]*onerror/gi,
    /<svg[^>]*onload/gi
  ];

  return dangerousPatterns.some((pattern) => pattern.test(input));
};

interface SanitizeOptions {
  allowedTags?: string[];
  maxLength?: number;
  stripHtml?: boolean;
}

/**
 * 入力値のサニタイゼーション
 * @param input - サニタイズする入力値
 * @param options - オプション設定
 * @returns サニタイズされた文字列
 */
export const sanitizeInput = (input: unknown, options: SanitizeOptions = {}): unknown => {
  if (typeof input !== 'string') return input;

  const { allowedTags = [], maxLength = 5000, stripHtml = true } = options;

  let sanitized = input;

  // 最大長の制限
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
  }

  // HTMLタグの除去
  if (stripHtml && allowedTags.length === 0) {
    sanitized = sanitized.replace(/<[^>]*>/g, '');
  }

  // 危険な文字列パターンの除去
  sanitized = sanitized
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '')
    .replace(/eval\s*\(/gi, '')
    .replace(/expression\s*\(/gi, '');

  return sanitized.trim();
};

interface Session {
  loginTime?: string;
  [key: string]: any;
}

/**
 * セッションのセキュリティチェック
 * @param session - セッションオブジェクト
 * @returns セッションが有効な場合はtrue
 */
export const isSessionValid = (session: Session | null | undefined): boolean => {
  if (!session || !session.loginTime) return false;

  const SESSION_TIMEOUT = 30 * 60 * 1000; // 30分
  const currentTime = Date.now();
  const loginTime = new Date(session.loginTime).getTime();

  return currentTime - loginTime < SESSION_TIMEOUT;
};

/**
 * セキュアなランダム文字列の生成
 * @param length - 生成する文字列の長さ
 * @returns ランダムな文字列
 */
export const generateSecureToken = (length: number = 32): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const array = new Uint8Array(length);

  if (window.crypto && window.crypto.getRandomValues) {
    window.crypto.getRandomValues(array);
  } else {
    // フォールバック（あまりセキュアではない）
    for (let i = 0; i < length; i++) {
      array[i] = Math.floor(Math.random() * 256);
    }
  }

  return Array.from(array, (byte) => chars[byte % chars.length]).join('');
};

/**
 * ローカルストレージのセキュアな読み取り
 * @param key - 読み取るキー
 * @returns パースされた値またはnull
 */
export const secureGetItem = (key: string): any => {
  try {
    const item = localStorage.getItem(key);
    if (!item) return null;

    const parsed = JSON.parse(item);

    // セッションの場合は有効性をチェック
    if (key === 'authSession' && !isSessionValid(parsed)) {
      localStorage.removeItem(key);
      return null;
    }

    return parsed;
  } catch (error) {
    console.error('Error reading from localStorage:', error);
    return null;
  }
};

/**
 * ローカルストレージへのセキュアな書き込み
 * @param key - 書き込むキー
 * @param value - 書き込む値
 */
export const secureSetItem = (key: string, value: any): void => {
  try {
    // セッションの場合はタイムスタンプを追加
    if (key === 'authSession' && typeof value === 'object') {
      value.loginTime = new Date().toISOString();
    }

    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error('Error writing to localStorage:', error);
  }
};

/**
 * SQLインジェクション対策用の文字列エスケープ
 * @param input - エスケープする文字列
 * @returns エスケープされた文字列
 */
export const escapeSqlString = (input: unknown): unknown => {
  if (typeof input !== 'string') return input;

  // 危険な文字をエスケープ
  /* eslint-disable no-control-regex */
  return input
    .replace(/'/g, "''") // シングルクォートをエスケープ
    .replace(/"/g, '""') // ダブルクォートをエスケープ
    .replace(/\\/g, '\\\\') // バックスラッシュをエスケープ
    .replace(/\0/g, '\\0') // NULL文字をエスケープ
    .replace(/\n/g, '\\n') // 改行をエスケープ
    .replace(/\r/g, '\\r') // キャリッジリターンをエスケープ
    .replace(/\x1a/g, '\\Z'); // SUB文字をエスケープ
  /* eslint-enable no-control-regex */
};

/**
 * SQLインジェクションのパターンを検出
 * @param input - チェックする入力値
 * @returns 危険なパターンが含まれている場合はtrue
 */
export const detectSqlInjectionPatterns = (input: unknown): boolean => {
  if (typeof input !== 'string') return false;

  const dangerousSqlPatterns = [
    /(\b(union|select|insert|update|delete|drop|create|alter|exec|execute)\b)/gi,
    /(--|#|\/\*|\*\/)/, // SQLコメント
    /(\bor\b\s*\d+\s*=\s*\d+)/gi, // OR 1=1 パターン
    /(\band\b\s*\d+\s*=\s*\d+)/gi, // AND 1=1 パターン
    /(\bwaitfor\b\s+\bdelay\b)/gi, // タイミング攻撃
    /(\bxp_cmdshell\b)/gi, // システムコマンド実行
    /(\bexec\b\s*\()/gi, // 動的SQL実行
    /'\s*(or|and|union|select|delete|drop|;|--)/i, // シングルクォートに続くSQL危険語句
    /;\s*$/ // 文末のセミコロン
  ];

  return dangerousSqlPatterns.some((pattern) => pattern.test(input));
};

interface CSRFTokenData {
  token: string;
  timestamp: number;
  used: boolean;
}

// CSRF トークン管理
let csrfTokenStore = new Map<string, CSRFTokenData>();

/**
 * CSRF トークンを生成
 * @param sessionId - セッションID
 * @returns 生成されたCSRFトークン
 */
export const generateCSRFToken = (sessionId: string = 'default'): string => {
  const token = generateSecureToken(32);
  const timestamp = Date.now();

  csrfTokenStore.set(sessionId, {
    token,
    timestamp,
    used: false
  });

  // 古いトークンをクリーンアップ（1時間以上経過したもの）
  const oneHourAgo = timestamp - 60 * 60 * 1000;
  for (const [key, value] of csrfTokenStore.entries()) {
    if (value.timestamp < oneHourAgo) {
      csrfTokenStore.delete(key);
    }
  }

  return token;
};

/**
 * CSRF トークンを検証（検証成功時に自動的に使用済みにマーク）
 * @param token - 検証するトークン
 * @param sessionId - セッションID
 * @returns トークンが有効な場合はtrue
 */
export const validateCSRFToken = (token: string, sessionId: string = 'default'): boolean => {
  const tokenData = csrfTokenStore.get(sessionId);

  if (!tokenData || tokenData.token !== token) {
    return false;
  }

  // トークンの有効期限をチェック（1時間）
  const isExpired = Date.now() - tokenData.timestamp > 60 * 60 * 1000;
  if (isExpired) {
    csrfTokenStore.delete(sessionId);
    return false;
  }

  // ワンタイムトークンの場合
  if (tokenData.used) {
    return false;
  }

  // 検証成功時に即座に使用済みにマーク（セキュリティ向上）
  tokenData.used = true;
  csrfTokenStore.set(sessionId, tokenData);

  return true;
};

/**
 * 電話番号のサニタイゼーション
 * @param phone - 電話番号
 * @returns サニタイズされた電話番号
 */
export const sanitizePhoneNumber = (phone: unknown): string => {
  if (typeof phone !== 'string') return '';

  // 数字とハイフンのみを許可し、その他の文字を除去
  let sanitized = phone.replace(/[^\d-]/g, '');

  // ハイフンを除去（統一フォーマット）
  sanitized = sanitized.replace(/-/g, '');

  // 日本の電話番号形式をチェック（10桁または11桁）
  if (!/^0\d{9,10}$/.test(sanitized)) {
    return ''; // 無効な形式の場合は空文字を返す
  }

  return sanitized;
};

/**
 * メールアドレスのサニタイゼーション
 * @param email - メールアドレス
 * @returns サニタイズされたメールアドレス
 */
export const sanitizeEmail = (email: unknown): string => {
  if (typeof email !== 'string') return '';

  // 基本的なサニタイゼーション
  let sanitized = email.trim().toLowerCase();

  // 危険な文字を除去（シングルクォートは有効なので除外）
  sanitized = sanitized.replace(/[<>"]/g, '');

  // 基本的なメールフォーマットチェック（シングルクォートを許可）
  const emailRegex = /^[a-zA-Z0-9._%+'"-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (!emailRegex.test(sanitized)) {
    return ''; // 無効な形式の場合は空文字を返す
  }

  return sanitized;
};

interface CSPPolicy {
  [directive: string]: string[];
}

interface CSPOptions {
  [directive: string]: string[];
}

/**
 * Content Security Policy のヘッダー値を生成
 * @param options - CSP設定オプション
 * @returns CSPヘッダー値
 */
export const generateCSPHeader = (options: CSPOptions = {}): string => {
  const isDevelopment = process.env.NODE_ENV === 'development';

  // 本番環境用の厳格なポリシー
  const productionPolicy: CSPPolicy = {
    'default-src': ["'self'"],
    'script-src': ["'self'"],
    'style-src': ["'self'"],
    'img-src': ["'self'", 'data:', 'https:'],
    'font-src': ["'self'"],
    'connect-src': ["'self'", 'https://api.mypayy.com'],
    'object-src': ["'none'"],
    'frame-ancestors': ["'none'"],
    'base-uri': ["'self'"],
    'form-action': ["'self'"]
  };

  // 開発環境用の緩和されたポリシー
  const developmentPolicy: CSPPolicy = {
    'default-src': ["'self'"],
    'script-src': ["'self'", "'unsafe-inline'", "'unsafe-eval'"],
    'style-src': ["'self'", "'unsafe-inline'"],
    'img-src': ["'self'", 'data:', 'https:'],
    'font-src': ["'self'"],
    'connect-src': ["'self'", 'http://localhost:8000', 'https://api.mypayy.com'],
    'object-src': ["'none'"],
    'frame-ancestors': ["'none'"],
    'base-uri': ["'self'"],
    'form-action': ["'self'"]
  };

  const defaultPolicy = isDevelopment ? developmentPolicy : productionPolicy;
  const policy = { ...defaultPolicy, ...options };

  return Object.entries(policy)
    .map(([directive, sources]) => `${directive} ${sources.join(' ')}`)
    .join('; ');
};

interface SecurityHeaders {
  [header: string]: string;
}

/**
 * セキュリティヘッダーの設定値を取得
 * @returns セキュリティヘッダーのキーと値
 */
export const getSecurityHeaders = (): SecurityHeaders => {
  return {
    'Content-Security-Policy': generateCSPHeader(),
    'X-Content-Type-Options': 'nosniff',
    'X-Frame-Options': 'DENY',
    'X-XSS-Protection': '1; mode=block',
    'Referrer-Policy': 'strict-origin-when-cross-origin',
    'Permissions-Policy': 'geolocation=(), microphone=(), camera=()'
  };
};
